import { useCallback, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { FTUXType } from '../types/fd-ftux.types'

interface ScaleCalculationProps {
  totalSteps: number
  currentStep: number
  parentContainerRef: React.RefObject<HTMLDivElement>
  renderContent: (step: number) => React.ReactNode
}

export const usePreRenderScales = ({
  totalSteps,
  currentStep,
  parentContainerRef,
  renderContent
}: ScaleCalculationProps) => {
  const scaleMap = useRef<Record<number, number>>({})

  const calculateScale = useCallback(
    (container: HTMLDivElement, parent: HTMLDivElement) => {
      // Reset any previous scaling
      container.style.transform = 'scale(1)'
      parent.style.paddingBottom = '24px'

      // Get initial dimensions
      const bodyRect = container.getBoundingClientRect()
      const parentRect = parent.getBoundingClientRect()

      // Calculate available space (accounting for padding)
      const availableHeight = parentRect.height - 48 // 24px padding top and bottom

      // Check if content fits in height
      const heightFits = bodyRect.height <= availableHeight

      // If content fits in height, no scaling needed
      if (heightFits) {
        return 1
      }

      // Calculate scale factor for height
      const scale = availableHeight / bodyRect.height

      // Apply padding if we're scaling down
      if (scale < 1) {
        parent.style.paddingBottom = '20px'
      }

      return scale
    },
    [currentStep]
  )

  const preRenderStep = useCallback(
    (step: number) => {
      if (step < 0 || step >= totalSteps) return

      const root = document.createElement('div')
      document.body.appendChild(root)
      const parentContainer = parentContainerRef.current
      if (!parentContainer) return

      const reactRoot = createRoot(root)
      reactRoot.render(
        <div className='relative w-full h-max flex justify-center items-center'>{renderContent(step)}</div>
      )

      // Wait for the next frame to ensure React has rendered
      requestAnimationFrame(() => {
        // Add a small delay to ensure the DOM is updated
        setTimeout(() => {
          const container = root.firstChild as HTMLDivElement
          if (container) {
            const scale = calculateScale(container, parentContainer)
            scaleMap.current[step] = scale
            reactRoot.unmount()
            document.body.removeChild(root)
          }
        }, 0)
      })
    },
    [totalSteps, calculateScale, renderContent, parentContainerRef]
  )

  // Pre-render only next step
  useEffect(() => {
    if (currentStep + 1 < totalSteps) {
      preRenderStep(currentStep + 1)
    }
  }, [currentStep, totalSteps, preRenderStep])

  // Calculate and apply scale for current step
  useEffect(() => {
    // Ensure current step is within bounds
    if (currentStep < 0 || currentStep >= totalSteps) return

    const parentContainer = parentContainerRef.current
    const bodyContainer = parentContainer?.querySelector('#body-container') as HTMLDivElement | null
    if (!parentContainer || !bodyContainer) return

    // Create a temporary container to measure the content
    const tempContainer = document.createElement('div')
    tempContainer.style.position = 'absolute'
    tempContainer.style.visibility = 'hidden'
    tempContainer.style.width = '100%'
    tempContainer.style.padding = '0'
    tempContainer.style.margin = '0'
    document.body.appendChild(tempContainer)

    const reactRoot = createRoot(tempContainer)
    reactRoot.render(
      <div className='relative w-full h-max flex justify-center items-center'>{renderContent(currentStep)}</div>
    )

    // Wait for the next frame to ensure React has rendered
    requestAnimationFrame(() => {
      // Add a small delay to ensure the DOM is updated
      setTimeout(() => {
        const contentContainer = tempContainer.firstChild as HTMLDivElement
        if (contentContainer) {
          // Reset any previous scaling
          bodyContainer.style.transform = 'scale(1)'
          parentContainer.style.paddingBottom = '24px'

          // Calculate scale using the temporary container
          const scale = calculateScale(contentContainer, parentContainer)
          scaleMap.current[currentStep] = scale

          // Apply the scale
          bodyContainer.style.transform = `scale(${scale})`
          bodyContainer.style.transformOrigin = 'center center'
          if (scale < 1) {
            parentContainer.style.paddingBottom = '20px'
          } else {
            parentContainer.style.paddingBottom = '24px'
          }

          // Clean up
          reactRoot.unmount()
          document.body.removeChild(tempContainer)
        }
      }, 0)
    })
  }, [currentStep, calculateScale, parentContainerRef, renderContent, totalSteps])
}
